import { Suspense, lazy, ReactNode, Fragment, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { PartialRouteObject, useNavigate } from 'react-router';
import SidebarLayout from 'src/layouts/SidebarLayout';
import BaseLayout from 'src/layouts/BaseLayout';
import SuspenseLoader from 'src/components/SuspenseLoader';
import { userHasAccessToPage } from './utils/access';
import {
  CURRENT_USER
} from './constants';
import FicheEEPBM from './content/pages/Components/FicheEEPBM';
import { authService } from './services';

const Loader = (Component) => (props) => (
  <Suspense fallback={<SuspenseLoader />}>
    <Component {...props} />
  </Suspense>
);

function RolesAuthRoute({ children, page }: { children: ReactNode, page: string }) {
  caches.keys().then((names) => {
    names.forEach((name) => {
      caches.delete(name);
    });
  });
  let navigate = useNavigate();
  useEffect(() => {
    authService.getUserData().then(response => {
      if (response.data.success) {
        if (response.data.data !== null) {
          localStorage.setItem(CURRENT_USER, JSON.stringify(response.data.data));
          localStorage.setItem("sessionStart", Date.now().toString());
          if (window.location.pathname == '/') {
            if (response.data.data.role.id > 2) {
              navigate("/page/management", { replace: true });
            } else {
              navigate("/page/accueil", { replace: true });
            }
          }
        } else {
          localStorage.removeItem(CURRENT_USER);
          localStorage.removeItem("sessionStart");
          localStorage.removeItem("lastActivityAt");
          navigate("/", { replace: true });
        }
      } else {
        localStorage.removeItem(CURRENT_USER);
        localStorage.removeItem("sessionStart");
        localStorage.removeItem("lastActivityAt");
        navigate("/", { replace: true });
      }
    }).catch(err => {
      localStorage.removeItem(CURRENT_USER);
      localStorage.removeItem("sessionStart");
      localStorage.removeItem("lastActivityAt");
      navigate("/", { replace: true });
    });
  }, []);

  const canAccess = userHasAccessToPage(page);
  if (canAccess) {
    return (
      <Fragment>
        {children}
      </Fragment>
    );
  }
  return (
    <Fragment>
      <Status403></Status403>
    </Fragment>
  );
}
// Pages
const Login = Loader(lazy(() => import('src/content/Login')));
const LoginOnBoarding = Loader(lazy(() => import('src/content/OnBoarding')));
const ResetOnBoarding = Loader(lazy(() => import('src/content/OnBoarding/reset')));

// Applications
const Competence = Loader(lazy(() => import('src/content/pages/Components/Competences')));
const ListeKP = Loader(lazy(() => import('src/content/pages/Components/ListeKP')));
const KeyPeople = Loader(lazy(() => import('src/content/pages/Components/ListeKP/KeyPeople')));
const ListSkills = Loader(lazy(() => import('src/content/pages/Components/ListSkills')));
const ListOtherSkills = Loader(lazy(() => import('src/content/pages/Components/ListOtherSkills')));
const ListeEEP = Loader(lazy(() => import('src/content/pages/Components/ListeEEP')));
const Candidat = Loader(lazy(() => import('src/content/pages/Components/Candidat')));
const Collaborateur = Loader(lazy(() => import('src/content/pages/Components/Collaborateurs')));
const MyManager = Loader(lazy(() => import('src/content/pages/Components/MyManager')));
const FormationsCertifications = Loader(lazy(() => import('src/content/pages/Components/FormationsCertifications')));
const FormationCertification = Loader(lazy(() => import('src/content/pages/Components/FormationsCertifications/FormationCertification')));
const NewTemplate = Loader(lazy(() => import('src/content/pages/Components/FormationsCertifications/FormationCertification/NewTemplate')));
const ExportCertifications = Loader(lazy(() => import('src/content/pages/Components/ExportCertifications')));
const ExportCompetences = Loader(lazy(() => import('src/content/pages/Components/ExportCompetences')));
const DroitDAcces = Loader(lazy(() => import('src/content/pages/Components/DroitDAcces')));
const ListEEPManager = Loader(lazy(() => import('src/content/pages/Components/ListEEPManager')));
const LogsCv2Dc = Loader(lazy(() => import('src/content/pages/Components/LogsCv2Dc')));

// Components
const Accueil = Loader(lazy(() => import('src/content/pages/Components/Accueil')));
const DCMenu = Loader(lazy(() => import('src/content/pages/Components/Accueil/DCMenu')));
const EAMenu = Loader(lazy(() => import('src/content/pages/Components/Accueil/EAMenu')));
const ManagerMenu = Loader(lazy(() => import('src/content/pages/Components/Accueil/ManagerMenu')));
const AdministrationMenu = Loader(lazy(() => import('src/content/pages/Components/Accueil/ManagerMenu/AdministrationMenu')));
const CreateDC = Loader(lazy(() => import('src/content/pages/Components/CreateDC')));
const FicheDC = Loader(lazy(() => import('src/content/pages/Components/FicheDC')));
const FicheEA = Loader(lazy(() => import('src/content/pages/Components/FicheEA')));
const FicheDCExt = Loader(lazy(() => import('src/content/pages/Components/FicheDC/FicheDCExt')));

// Status
const Status403 = Loader(lazy(() => import('src/content/pages/Status/Status403')));
const Status404 = Loader(lazy(() => import('src/content/pages/Status/Status404')));
const Status500 = Loader(lazy(() => import('src/content/pages/Status/Status500')));
const StatusComingSoon = Loader(lazy(() => import('src/content/pages/Status/ComingSoon')));
const StatusMaintenance = Loader(lazy(() => import('src/content/pages/Status/Maintenance')));

const ASMDocument = Loader(lazy(() => import('src/components/ASMDocument')));

const {
  REACT_APP_DEV_ONBOARDINGURL,
  REACT_APP_PP_ONBOARDINGURL,
  REACT_APP_PROD_ONBOARDINGURL,
} = process.env;

const protocol = window.location.protocol;
const domain = window.location.hostname;
const port = window.location.port;

const url = `${protocol}//${domain}${port ? ":" + port : ""}`;
const isOnBoarding = (url === REACT_APP_DEV_ONBOARDINGURL) || (url === REACT_APP_PP_ONBOARDINGURL) || (url === REACT_APP_PROD_ONBOARDINGURL);

const routes: PartialRouteObject[] = [
  {
    path: '*',
    element: <BaseLayout />,
    children: [
      {
        path: '/',
        element: isOnBoarding ? <LoginOnBoarding /> : <Login />
        // element: <LoginOnBoarding />
      },
      {
        path: '/sf',
        element: <FicheDCExt />
      },
      {
        path: '/reset',
        element: <ResetOnBoarding />
      },
      {
        path: 'status',
        element: (
          <SidebarLayout />
        ),
        children: [
          {
            path: '/',
            element: (
              <Navigate
                to="404"
                replace
              />
            )
          },
          {
            path: '403',
            element: <Status403 />
          },
          {
            path: '404',
            element: <Status404 />
          },
          {
            path: '500',
            element: <Status500 />
          },
          {
            path: 'maintenance',
            element: <StatusMaintenance />
          },
          {
            path: 'coming-soon',
            element: <StatusComingSoon />
          },
        ]
      },
      {
        path: '*',
        element: <Status404 />
      },
    ]
  },
  {
    path: 'management',
    element: (
      <SidebarLayout />
    ),
    children: [
      {
        path: '/',
        element: (
          <RolesAuthRoute page={'managementSkills'} >
            <Navigate
              to="/management/competence"
              replace
            />
          </RolesAuthRoute >
        )
      },
      {
        path: 'competence',
        element:
          <RolesAuthRoute page={'managementSkills'} >
            <Competence />
          </RolesAuthRoute >
      },
      {
        path: 'listeKP',
        element:
          <RolesAuthRoute page={'managementAdministration'} >
            <ListeKP />
          </RolesAuthRoute >
      },
      {
        path: 'listSkills',
        element:
          <RolesAuthRoute page={'managementAdministration'} >
            <ListSkills />
          </RolesAuthRoute >
      },
      {
        path: 'listOtherSkills',
        element:
          <RolesAuthRoute page={'managementAdministration'} >
            <ListOtherSkills />
          </RolesAuthRoute >
      },
      {
        path: 'collaborateur',
        element:
          <RolesAuthRoute page={'managementCollaborators'} >
            <Collaborateur />
          </RolesAuthRoute >
      },
      {
        path: 'manager',
        element:
          <RolesAuthRoute page={'manageManager'} >
            <MyManager />
          </RolesAuthRoute >
      },
      {
        path: 'formationsCertifications/template',
        element:
          <RolesAuthRoute page={'manipTrainingModel'} >
            <NewTemplate />
          </RolesAuthRoute>
      },
      {
        path: 'formationsCertifications/template/:id',
        element:
          <RolesAuthRoute page={'manipTrainingModel'} >
            <NewTemplate />
          </RolesAuthRoute>
      },
      {
        path: 'formationsCertifications/template-model/:idTrainingTemplate',
        element:
          <RolesAuthRoute page={'manipTrainingModel'} >
            <NewTemplate />
          </RolesAuthRoute>
      },
      {
        path: 'formationsCertifications/:id',
        element:
          <RolesAuthRoute page={'managementAdministration'} >
            <FormationCertification />
          </RolesAuthRoute>
      },
      {
        path: 'formationsCertifications/:id/:action',
        element:
          <RolesAuthRoute page={'managementAdministration'} >
            <FormationCertification />
          </RolesAuthRoute>
      },
      {
        path: 'formationsCertifications',
        element:
          <RolesAuthRoute page={'managementAdministration'} >
            <FormationsCertifications />
          </RolesAuthRoute>
      },
      {
        path: 'exportCertifications',
        element:
          <RolesAuthRoute page={'exportCertifications'} >
            <ExportCertifications />
          </RolesAuthRoute >
      },
      {
        path: 'logsCv2Dc',
        element:
          <RolesAuthRoute page={'logsCv2Dc'} >
            <LogsCv2Dc />
          </RolesAuthRoute >
      },
      {
        path: 'exportCompetences',
        element:
          <RolesAuthRoute page={'managementAdministration'} >
            <ExportCompetences />
          </RolesAuthRoute >
      },
      {
        path: 'droitDAcces',
        element:
          <RolesAuthRoute page={'droitDAcces'} >
            <DroitDAcces />
          </RolesAuthRoute >
      },
      // key-people
      {
        path: 'key-people/:idUser',
        element:
          <RolesAuthRoute page={'managementAdministration'} >
            <KeyPeople />
          </RolesAuthRoute >
      },
      {
        path: 'candidat',
        element:
          <RolesAuthRoute page={'managementCandidats'} >
            <Candidat />
          </RolesAuthRoute >
      },
      {
        path: 'listeEEP',
        element:
          <RolesAuthRoute page={'managementAdministration'} >
            <ListeEEP />
          </RolesAuthRoute >
      },
      {
        path: 'listEEPManager',
        element:
          <RolesAuthRoute page={'manageManager'} >
            <ListEEPManager />
          </RolesAuthRoute >
      },
    ]
  },
  {
    path: 'page',
    element: (
      <SidebarLayout isFicheDc={true} />
    ),
    children: [
      {
        path: 'dc/fiche/:id/:idLang',
        element:
          <RolesAuthRoute page={'ficheDc'} >
            <FicheDC />
          </RolesAuthRoute >
      },
      {
        path: 'dc/create/:idUser/:idLang',
        element:
          <RolesAuthRoute page={'createDc'} >
            <CreateDC />
          </RolesAuthRoute >
      }
    ]
  },
  {
    path: 'page',
    element: (
      <SidebarLayout />
    ),
    children: [
      {
        path: 'accueil',
        element:
          <RolesAuthRoute page={'menuDc'} >
            <Accueil />
          </RolesAuthRoute >
      },
      {
        path: 'ea/fiche/:year/:id',
        element:
          <RolesAuthRoute page={'ficheEa'} >
            <FicheEA />
          </RolesAuthRoute >
      },
      {
        path: 'eep/bm/fiche/:year/:id',
        element:
          <RolesAuthRoute page={'ficheEepBm'} >
            <FicheEEPBM />
          </RolesAuthRoute >
      },
      {
        path: 'dc/menu',
        element:
          <RolesAuthRoute page={'menuDc'} >
            <DCMenu />
          </RolesAuthRoute >
      },
      {
        path: 'ea/menu',
        element:
          <RolesAuthRoute page={'menuEa'} >
            <EAMenu />
          </RolesAuthRoute >
      },
      {
        path: 'management',
        element:
          <RolesAuthRoute page={'menuManagement'} >
            <ManagerMenu />
          </RolesAuthRoute >
      },
      {
        path: '/management/administration',
        element:
          <RolesAuthRoute page={'managementAdministration'} >
            <AdministrationMenu />
          </RolesAuthRoute >
      },
    ]
  },
  {
    path: 'document',
    element: (
      <SidebarLayout />
    ),
    children: [
      {
        path: 'pdf',
        element:
          <RolesAuthRoute page={'ficheEa'} >
            <ASMDocument />
          </RolesAuthRoute>
      }
    ]
  }
];

export default routes;
